import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, RotateCw } from "lucide-react";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import {
    createClientPoll,
    fetchClientPolls,
    markClientPollsViewed,
    voteClientPoll,
} from "../services/polls";
import { fetchClientAccessOverview } from "../services/profile";
import { fetchLocations } from "../services/locations";
import { isPastDateTimeLocal, toDateTimeLocalMinValue } from "../utils/dateInput";
import { fetchMyRestrictions } from "../services/clientRestrictions";
import RestrictionNotice from "../components/RestrictionNotice";
import { formatRestrictionMessage, isModuleReadonly } from "../utils/clientRestrictionsUi";
import "../styles/chat.css";

function formatKyiv(dt) {
    if (!dt) return "";
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("uk-UA", {
        timeZone: "Europe/Kyiv",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(d).replace(",", "");
}

const ClientPolls = ({ onPollsViewed }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");

    const [newQuestion, setNewQuestion] = useState("");
    const [newExpiresLocal, setNewExpiresLocal] = useState("");
    const [accessOverview, setAccessOverview] = useState({ contexts: [] });
    const [buildingLocations, setBuildingLocations] = useState([]);
    const [selectedTarget, setSelectedTarget] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [expandedPollId, setExpandedPollId] = useState(null);
    const [sectionFilter, setSectionFilter] = useState("active");
    const [highlightedPollId, setHighlightedPollId] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [myRestriction, setMyRestriction] = useState(null);
    const pollRefs = useRef(new Map());
    const pollsReadonly = isModuleReadonly(myRestriction, "polls");

    const connectedNetworks = useMemo(() => Object.values(
        (accessOverview?.contexts || []).reduce((acc, context) => {
            if (!context?.network_id) return acc;
            const key = String(context.network_id);
            if (!acc[key]) {
                acc[key] = {
                    id: context.network_id,
                    name: context.network_name || `Мережа #${context.network_id}`,
                };
            }
            return acc;
        }, {}),
    ), [accessOverview]);

    const targetOptions = useMemo(() => {
        const networkNameById = Object.fromEntries(
            connectedNetworks.map((network) => [String(network.id), network.name]),
        );

        return (buildingLocations || []).map((location) => {
            const networkId = Number(location.network_id || connectedNetworks[0]?.id || 0);
            const networkName = networkNameById[String(networkId)] || "Мережа";
            const locationLabel = [location.street, location.house_number, location.location_name]
                .filter(Boolean)
                .join(" ") || `Локація #${location.id}`;
            return {
                value: `location:${location.id}`,
                networkId,
                locationId: Number(location.id),
                label: `${networkName} • ${locationLabel}`,
            };
        });
    }, [buildingLocations, connectedNetworks]);

    const selectedTargetMeta = useMemo(
        () => targetOptions.find((option) => option.value === selectedTarget) || null,
        [targetOptions, selectedTarget],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [data, overview, locations] = await Promise.all([
                fetchClientPolls(),
                fetchClientAccessOverview(),
                fetchLocations().catch(() => []),
            ]);
            setItems(Array.isArray(data) ? data : []);
            setAccessOverview(overview || { contexts: [] });
            setBuildingLocations(Array.isArray(locations) ? locations : []);
        } catch (e) {
            setError(e?.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await markClientPollsViewed();
                if (!cancelled && typeof onPollsViewed === "function") onPollsViewed();
            } catch (_) {
                // ignore mark failures
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (targetOptions.length === 0) return;

        setSelectedTarget((prev) => {
            if (prev && targetOptions.some((option) => option.value === prev)) {
                return prev;
            }
            return targetOptions[0].value;
        });
    }, [targetOptions]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchMyRestrictions();
                if (!cancelled) setMyRestriction(data);
            } catch (_) {
                if (!cancelled) setMyRestriction(null);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const onChanged = () => load();
        window.addEventListener("wr_polls_changed", onChanged);
        return () => window.removeEventListener("wr_polls_changed", onChanged);
    }, [load]);

    useEffect(() => {
        if (!Array.isArray(items) || items.length === 0) return;

        let raw = null;
        try {
            raw = localStorage.getItem("pendingPollOpenId_client");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const pollId = parseInt(raw, 10);
        if (!Number.isFinite(pollId) || pollId <= 0) {
            try {
                localStorage.removeItem("pendingPollOpenId_client");
            } catch (_) {
                // ignore
            }
            return;
        }

        const item = items.find((poll) => Number(poll?.id) === pollId);
        if (!item) return;

        try {
            localStorage.removeItem("pendingPollOpenId_client");
        } catch (_) {
            // ignore
        }

        setFilterFrom("");
        setFilterTo("");
        const expiresAt = new Date(item?.expires_at).getTime();
        setSectionFilter(Number.isFinite(expiresAt) && expiresAt < Date.now() ? "history" : "active");
        setExpandedPollId(pollId);
        setHighlightedPollId(pollId);

        window.requestAnimationFrame(() => {
            pollRefs.current.get(pollId)?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        });

        const timer = window.setTimeout(() => {
            setHighlightedPollId((prev) => (prev === pollId ? null : prev));
        }, 60000);

        return () => window.clearTimeout(timer);
    }, [items]);

    const onCreate = useCallback(async () => {
        const question = (newQuestion || "").trim();
        if (question.length < 3) {
            setError("Питання занадто коротке");
            return;
        }

        if (!newExpiresLocal) {
            setError("Вкажіть кінцеву дату");
            return;
        }

        if (isPastDateTimeLocal(newExpiresLocal)) {
            setError("Дата «Активне до» не може бути в минулому");
            return;
        }

        let expiresIso = null;
        try {
            const d = new Date(newExpiresLocal);
            if (Number.isNaN(d.getTime())) throw new Error();
            expiresIso = d.toISOString();
        } catch (_) {
            setError("Некоректна кінцева дата");
            return;
        }

        if (!selectedTargetMeta?.networkId) {
            setError("Оберіть мережу або локацію для опитування");
            return;
        }

        if (pollsReadonly) {
            setError(formatRestrictionMessage(myRestriction, "polls"));
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            await createClientPoll({
                question,
                expires_at: expiresIso,
                network_id: selectedTargetMeta.networkId,
                location_id: selectedTargetMeta.locationId,
            });
            setNewQuestion("");
            setNewExpiresLocal("");
            setSelectedTarget(targetOptions[0]?.value || "");
            setShowCreateModal(false);
            await load();
        } catch (e) {
            setError(e?.message || "Не вдалося створити опитування");
        } finally {
            setSubmitting(false);
        }
    }, [load, newExpiresLocal, newQuestion, selectedTargetMeta, targetOptions, pollsReadonly, myRestriction]);

    const onVote = async (pollId, answerYes) => {
        setError(null);
        if (pollsReadonly) {
            setError(formatRestrictionMessage(myRestriction, "polls"));
            return;
        }
        try {
            await voteClientPoll({ poll_id: pollId, answer_yes: answerYes });
            await load();
        } catch (e) {
            setError(e?.message || "Не вдалося проголосувати");
        }
    };

    const filteredItems = useMemo(() => {
        const fromMs = filterFrom ? new Date(`${filterFrom}T00:00:00`).getTime() : null;
        const toMs = filterTo ? new Date(`${filterTo}T23:59:59.999`).getTime() : null;
        const hasFrom = Number.isFinite(fromMs);
        const hasTo = Number.isFinite(toMs);

        if (!hasFrom && !hasTo) return items || [];

        return (items || []).filter((poll) => {
            if (!poll || !poll.created_at) return true;
            const ms = new Date(poll.created_at).getTime();
            if (!Number.isFinite(ms)) return true;
            if (hasFrom && ms < fromMs) return false;
            if (hasTo && ms > toMs) return false;
            return true;
        });
    }, [items, filterFrom, filterTo]);

    const activePolls = useMemo(() => {
        const now = Date.now();
        return (filteredItems || []).filter((poll) => {
            const expires = new Date(poll?.expires_at).getTime();
            return !Number.isFinite(expires) || expires >= now;
        });
    }, [filteredItems]);

    const archivedPolls = useMemo(() => {
        const now = Date.now();
        return (filteredItems || []).filter((poll) => {
            const expires = new Date(poll?.expires_at).getTime();
            return Number.isFinite(expires) && expires < now;
        });
    }, [filteredItems]);

    const visibleActivePolls = sectionFilter === "history" ? [] : activePolls;
    const visibleArchivedPolls = sectionFilter === "active" ? [] : archivedPolls;
    const filterActive = Boolean(sectionFilter !== "active" || filterFrom || filterTo);

    const getPollPreview = (text) => {
        const raw = String(text || "").replace(/\s+/g, " ").trim();
        if (!raw) return "Без тексту";
        return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
    };

    const renderPollCard = (poll, variant) => {
        const isExpanded = expandedPollId === poll.id;
        const responded = Number(poll?.responded_count || 0);
        const totalClients = Number(poll?.total_clients_network || 0);
        const totalOperators = Number(poll?.total_operators_network || 0);
        const total = totalClients + totalOperators;
        const respondedClients = Number(poll?.responded_clients_count || 0);
        const respondedBoard = Number(poll?.responded_board_count || 0);
        const myVote = poll?.my_vote_yes;
        const canVote = poll?.can_vote === true && variant === "active" && !pollsReadonly;

        return (
            <div
                key={poll.id}
                ref={(el) => {
                    if (el) pollRefs.current.set(poll.id, el);
                    else pollRefs.current.delete(poll.id);
                }}
                className={`operator-accordion-item ${highlightedPollId === poll.id ? "is-highlighted" : ""}`}
            >
                <button type="button" className="operator-accordion-head" onClick={() => setExpandedPollId((prev) => prev === poll.id ? null : poll.id)}>
                    <div className="operator-accordion-head-main">
                        <div className="operator-accordion-title">{poll.question}</div>
                        <div className="operator-accordion-meta">
                            <span>{poll.location_name ? poll.location_name : "Вся мережа"}</span>
                            <span>{variant === "active" ? `До ${formatKyiv(poll.expires_at)}` : `Завершено ${formatKyiv(poll.expires_at)}`}</span>
                            <span>Відповіли {responded}{total ? ` / ${total}` : ""}</span>
                        </div>
                        <div className="operator-accordion-preview">{getPollPreview(poll.question)}</div>
                    </div>
                    <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                </button>

                {isExpanded ? (
                    <div className="operator-accordion-body">
                        <div className="operator-detail-grid">
                            <div className="operator-detail-block">
                                <span className="operator-detail-label">Участь</span>
                                <div className="operator-detail-value">Відповіли: {responded}{total ? ` / ${total}` : ""}</div>
                            </div>
                            <div className="operator-detail-block">
                                <span className="operator-detail-label">Результат</span>
                                <div className="operator-detail-value">Так: {Number(poll?.yes_count || 0)} · Ні: {Number(poll?.no_count || 0)}</div>
                            </div>
                            <div className="operator-detail-block operator-detail-block--full">
                                <span className="operator-detail-label">Розподіл</span>
                                <div className="operator-detail-value">Клієнтів: {respondedClients}/{totalClients} · Правління: {respondedBoard}/{totalOperators}</div>
                            </div>
                            {myVote === true ? <div className="operator-detail-note">Ваша відповідь: Так</div> : null}
                            {myVote === false ? <div className="operator-detail-note">Ваша відповідь: Ні</div> : null}
                        </div>
                        {canVote ? (
                            <div className="operator-stack-actions" style={{ marginTop: 12 }}>
                                <button className="add-btn" onClick={() => onVote(poll.id, true)}>Так</button>
                                <button className="add-btn add-btn--secondary" onClick={() => onVote(poll.id, false)}>Ні</button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <div className="wr-polls-page operator-page-width-chat">
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        className="add-btn toolbar-icon-btn"
                        type="button"
                        onClick={() => {
                            setError(null);
                            if (pollsReadonly) {
                                setError(formatRestrictionMessage(myRestriction, "polls"));
                                return;
                            }
                            setShowCreateModal(true);
                        }}
                        title="Створити опитування"
                        aria-label="Створити опитування"
                        disabled={pollsReadonly}
                    >
                        <Plus size={16} />
                    </button>
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        type="button"
                        onClick={load}
                        disabled={loading}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={filterActive}
                        title="Фільтри опитувань"
                        hint="За замовчуванням показано активні. Період — за датою створення."
                        buttonTitle="Фільтри опитувань"
                        onClear={() => {
                            setSectionFilter("active");
                            setFilterFrom("");
                            setFilterTo("");
                        }}
                    >
                        <label className="toolbar-filter-field">
                            <span>Розділ</span>
                            <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
                                <option value="active">Активні ({activePolls.length})</option>
                                <option value="history">Історія ({archivedPolls.length})</option>
                                <option value="all">Усі</option>
                            </select>
                        </label>
                        <DateRangeFields
                            from={filterFrom}
                            to={filterTo}
                            onFromChange={setFilterFrom}
                            onToChange={setFilterTo}
                        />
                    </FilterPopover>
                </div>
            </div>

            <RestrictionNotice restriction={myRestriction} module="polls" />

            {showCreateModal ? (
                <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Створити опитування</h3>
                        <RestrictionNotice restriction={myRestriction} module="polls" />
                        <textarea
                            rows={3}
                            className="wr-polls-create-textarea"
                            placeholder="Питання..."
                            value={newQuestion}
                            onChange={(e) => setNewQuestion(e.target.value)}
                        />
                        <div className="wr-polls-create-inline-row">
                            <label className="wr-polls-create-field">
                                <span>Кому показати</span>
                                <select value={selectedTarget} onChange={(e) => setSelectedTarget(e.target.value)}>
                                    <option value="">Оберіть мережу або локацію</option>
                                    {targetOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="wr-polls-create-field">
                                <span>Активне до</span>
                                <input
                                    className="wr-date-input"
                                    type="datetime-local"
                                    value={newExpiresLocal}
                                    min={toDateTimeLocalMinValue()}
                                    onChange={(e) => setNewExpiresLocal(e.target.value)}
                                />
                            </label>
                        </div>
                        {error ? <div style={{ marginTop: 8 }}>{error}</div> : null}
                        <div className="modal-actions">
                            <button className="add-btn" onClick={onCreate} disabled={submitting}>
                                {submitting ? "Створення..." : "Створити"}
                            </button>
                            <button
                                className="add-btn add-btn--secondary"
                                type="button"
                                onClick={() => setShowCreateModal(false)}
                                disabled={submitting}
                            >
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {error && !showCreateModal ? <div style={{ marginBottom: 8 }}>{error}</div> : null}

            {loading ? <div>Завантаження...</div> : null}
            {!loading && (!Array.isArray(items) || items.length === 0) ? <div>Опитувань поки немає</div> : null}
            {!loading && Array.isArray(items) && items.length > 0 && (!Array.isArray(filteredItems) || filteredItems.length === 0) ? <div>Нічого не знайдено за вибраний період</div> : null}

            {(visibleActivePolls || []).length > 0 ? (
                <div className="list-card" style={{ marginBottom: 12 }}>
                    <h3 style={{ marginTop: 0 }}>Активні опитування</h3>
                    <div className="operator-accordion-list">
                        {visibleActivePolls.map((poll) => renderPollCard(poll, "active"))}
                    </div>
                </div>
            ) : null}

            {(visibleArchivedPolls || []).length > 0 ? (
                <div className="list-card">
                    <h3 style={{ marginTop: 0 }}>Історія опитувань</h3>
                    <div className="operator-accordion-list">
                        {visibleArchivedPolls.map((poll) => renderPollCard(poll, "archived"))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ClientPolls;
